import { LitElement, html, TemplateResult } from 'lit';
import { property, state, query } from 'lit/decorators.js';
import { stylesToast } from '../view/styles';
import { ToastActionButtonMode, ToastEvents, ToastSeverity } from '../type';
import { ButtonColor, ButtonSize, FontSize } from '../../gzIconButton/types';
import { ButtonColors, ButtonThemes, ButtonTypes } from '../../gzButton/types';
import { LinkColor, LinkType, Target } from '../../gzLinkComponent/types';

export class GzToastElement extends LitElement {
  @property({ type: String, attribute: true, reflect: true })
  message: string;

  @property({ type: String, attribute: true, reflect: true })
  severity: ToastSeverity;

  @property({ type: Boolean, attribute: true, reflect: true })
  hasAction: boolean;

  @property({ type: Boolean, attribute: true, reflect: true })
  keepOpen = false;

  @property({ type: Boolean, attribute: true, reflect: true })
  canClose = true;

  @property({ type: Number, attribute: true, reflect: true })
  autoCloseDelayMs = 10000;

  @property({ type: String, attribute: true, reflect: true })
  actionButtonText = 'Action';

  @property({ type: String, attribute: true, reflect: true })
  actionButtonMode = ToastActionButtonMode.BUTTON;

  @property({ type: Boolean, attribute: true, reflect: true })
  showInlineButton = true;

  @state()
  private readonly toastSeverity: ToastSeverity;

  @query('.action-button')
  actionButton!: HTMLElement;

  actionButtonCallback?: null | EventListenerOrEventListenerObject | string;

  @state()
  private readonly classIcon = new Map<ToastSeverity, String>([
    [ToastSeverity.NEUTRAL, ''],
    [ToastSeverity.INFORMATIVE, 'ds-icon-notification_info'],
    [ToastSeverity.WARNING, 'ds-icon-notification_warning'],
    [ToastSeverity.ERROR, 'ds-icon-notification_negative'],
    [ToastSeverity.SUCCESS, 'ds-icon-notification_success'],
  ]);

  static get styles() {
    return stylesToast;
  }

  public connectedCallback(): void {
    super.connectedCallback();
    this.autoCloseHandler();
  }

  attributeChangedCallback(name: string, oldVal: string | null, newVal: string | null): void {
    super.attributeChangedCallback(name, oldVal, newVal);
    if (name === 'hasAction' && newVal) {
      if (typeof this.actionButtonCallback === 'function') {
        this.actionButton.addEventListener('click', this.actionButtonCallback);
      }
    }
  }

  render(): TemplateResult {
    return html`
      <div id="toastNotification" class="toast-notification ${this.severity}">
        <gz-icon class="icon" name="${this.classIcon.get(this.severity)}" size="${ButtonSize.SIZE_24}"></gz-icon>
        <div class="message-container">
          <div class="message"><span>${this.message}${this.renderLinkButton()}</span></div>
          ${this.renderActionButton()}
        </div>
        <div class="separator"></div>
        <gz-icon-button
          @click="${this.closeHandler}"
          buttonColor="${this.severity === ToastSeverity.WARNING ? 'var(--main-titles-inputs-603)' : ButtonColor.WHITE}"
          icon="ds-icon-clear"
          fontSize="${FontSize.ICON_SIZE_18}"
          class="close-button"
        ></gz-icon-button>
      </div>
    `;
  }

  closeHandler(): void {
    this.dispatchEvent(new CustomEvent(ToastEvents.CLOSED));
    this.remove();
  }

  autoCloseHandler(): void {
    if (this.keepOpen) {
      return;
    }

    setTimeout(() => {
      this.closeHandler();
    }, this.autoCloseDelayMs);
  }

  private renderLinkButton(): TemplateResult | undefined {
    if (!this.hasAction || this.actionButtonMode !== ToastActionButtonMode.LINK) {
      return;
    }

    if (typeof this.actionButtonCallback === 'string') {
      return html`
        <gz-link
          href="${this.actionButtonCallback}"
          target="${Target.SELF}"
          color="${this.severity === ToastSeverity.WARNING ? LinkColor.DARK : LinkColor.LIGHT}"
          type="${LinkType.UNDERLINE}"
        >
          <span>${this.actionButtonText}</span>
        </gz-link>
      `;
    } else {
      return html`
        <gz-link
          @click="${this.actionButtonCallback}"
          color="${this.severity === ToastSeverity.WARNING ? LinkColor.DARK : LinkColor.LIGHT}"
          type="${LinkType.UNDERLINE}"
        >
          <span>${this.actionButtonText}</span>
        </gz-link>
      `;
    }
  }

  private renderActionButton(): TemplateResult | undefined {
    if (!this.hasAction || this.actionButtonMode !== ToastActionButtonMode.BUTTON) {
      return;
    }
    return html`
      <gz-button
        @click="${this.actionButtonCallback}"
        theme="${ButtonThemes.DARK}"
        color="${this.severity === ToastSeverity.WARNING ? ButtonColors.SECONDARY : ButtonColors.WHITE}"
        type="${ButtonTypes.OUTLINE}"
        class="action-button"
      >
        ${this.actionButtonText}
      </gz-button>
    `;
  }

  private renderCloseButton(): TemplateResult | undefined {
    if (!this.canClose) {
      return;
    }

    return html`
      <gz-icon-button
        @click="${this.closeHandler}"
        buttonColor="${this.severity === ToastSeverity.WARNING ? 'var(--body-text-arrows-400)' : ButtonColor.WHITE}"
        icon="ds-icon-clear"
        fontSize="${FontSize.ICON_SIZE_18}"
        class="close-button"
      ></gz-icon-button>
    `;
  }
}
